import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { categoryAPI } from '../services/api';

const CategoriesPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (shopId) {
      fetchCategories();
    }
  }, [shopId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await categoryAPI.getCategories(shopId);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          sx={{ mb: 2 }}
        >
          Back to Shops
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2} 
        mb={3}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
        >
          {isMobile ? 'Back' : 'Back to Shops'}
        </Button>
        <Typography variant={isMobile ? "h5" : "h4"}>Categories</Typography>
      </Box>

      {categories.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <CategoryIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" gutterBottom>
            No categories found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This shop doesn't have any categories yet.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {categories.map((category) => (
            <Card key={category._id} sx={{ width: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CategoryIcon color="primary" />
                  <Typography variant="h6" fontWeight="medium">
                    {category.name}
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created By
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {category.createdBy?.name || 'Unknown'}
                    </Typography>
                    {category.createdBy?.email && (
                      <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                        {category.createdBy.email}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Updated By
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {category.lastUpdatedBy?.name || '-'}
                    </Typography>
                    {category.lastUpdatedBy?.email && (
                      <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                        {category.lastUpdatedBy.email}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created At
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatDate(category.createdAt)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatDate(category.lastUpdatedAt)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px', sm: '16px' },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Created By</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Updated By</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CategoryIcon color="primary" />
                      <Typography variant="body1" fontWeight="medium">
                        {category.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {category.createdBy?.name || 'Unknown'}
                    {category.createdBy?.email && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {category.createdBy.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {category.lastUpdatedBy?.name || '-'}
                    {category.lastUpdatedBy?.email && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {category.lastUpdatedBy.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(category.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(category.lastUpdatedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CategoriesPage;


